import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import styled from 'styled-components'
import { Button } from 'sutra/components/Button'
import { Heading } from 'sutra/components/Text/Heading'
import useResizeObserver from 'use-resize-observer'

import { handleException } from 'clients/exception-handling'
import { Fetcher } from 'components/Fetcher'
import { UserList } from 'components/UserList'
import { useAlertSharedUsersQuery } from 'features/alerts/hooks/use-alert-shared-users-query'
import { AlertResponse, AlertService, AlertType } from 'features/alerts/service'
import { AuthContext } from 'features/auth/auth-context'
import { CustomShareSelect } from 'src/components/custom-share-select/CustomShareSelect'
import mixpanel from 'trackers/mixpanel'

const defaultValues = {
  title: '',
  alert_type: 'Star Rating',
  trigger_type: 'Significant Negative',
  time_interval: 'Quarter',
  alert_scope: 'Brand',
  reviews_filter: 0,
  shared_users: [],
  search_terms: [],
  search_criteria: [],
  filters: [],
}

type Props = {
  alertData: AlertResponse
  setIsOpen: (isOpen: boolean) => void
  refetch: () => Promise<unknown>
  projectId: string
  alertService: AlertService
}

const ShareAlert: React.FC<Props> = ({
  alertData,
  setIsOpen,
  refetch,
  projectId,
  alertService,
}) => {
  const { alert, owners, viewers } = alertData || {}
  // TODO: recipients not returned on get all - should be returned for owned alerts
  // TODO: what happens when you click on an alert you don't own?
  // TODO: disable controls when alert is not owned

  const authContext = useContext(AuthContext)
  const currentUserEmail = authContext?.user?.attributes?.email
  const isOwner = alertData?.owners.some(
    (el) => el.email?.toLowerCase() === currentUserEmail?.toLowerCase(),
  )

  const [newRecipients, setNewRecipients] = useState<string[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [accessType, setAccessType] = useState<string>('viewer')

  const { data } = useAlertSharedUsersQuery(alertService, projectId)

  const allUsers = useMemo(() => {
    if (!data) return []
    return [...data.admins, ...data.shared_users, ...data.yogi_employees].sort()
  }, [data])

  const { reset } = useForm<AlertType>({
    defaultValues: useMemo(() => {
      return alert ? alert : defaultValues
    }, [alert]),
  })

  useEffect(() => {
    if (alert) {
      reset(alert)
    } else {
      reset(defaultValues)
    }
  }, [alert, reset])

  const manualSubmit = async (): Promise<void> => {
    mixpanel.track('alerts', {
      action: 'share',
      value: alert?.id,
    })
    setIsSubmitting(true)
    const body = {
      alert_id: alert?.id,
      as_owners: false,
      email_addresses: newRecipients,
    }
    try {
      await alertService.shareAlert(projectId, body)
      refetch().then(() => {
        setIsOpen(false)
        reset()
        setIsSubmitting(false)
        toast.success('Invite sent successfully')
      })
    } catch (_e) {
      handleException(_e, { operation: 'alert.share' })

      setIsSubmitting(false)
      toast.error('Error sending invite')
    }
  }

  const handleUnshare = async (email: string): Promise<void> => {
    mixpanel.track('alerts', {
      action: 'unshare',
      value: alert?.id,
    })
    setIsSubmitting(true)
    const body = {
      alert_id: alert?.id,
      email_addresses: [email],
    }
    try {
      await alertService.unshareAlert(projectId, body)
      refetch().then(() => {
        setIsSubmitting(false)
        toast.success('User removed successfully')
      })
    } catch (_e) {
      handleException(_e, { operation: 'alert.unshare' })

      setIsSubmitting(false)
      toast.error('Error removing user')
    }
  }

  const { ref: headerRef, height: headerHeight = 1 } =
    useResizeObserver<HTMLDivElement>()
  const { ref: footerRef, height: footerHeight = 1 } =
    useResizeObserver<HTMLDivElement>()

  return (
    <Container>
      <Header ref={headerRef}>
        <HeaderRow>
          <Heading as="h4" $level="5">
            Sharing &quot;{alert?.title}&quot;
          </Heading>
          <CloseTitle onClick={() => setIsOpen(false)}>
            <Heading as="h4" $level="5">
              X
            </Heading>
          </CloseTitle>
        </HeaderRow>
      </Header>
      <Body $headerHeight={headerHeight} $footerHeight={footerHeight}>
        <UnborderedSection>
          <ShareSelectWrapper>
            <CustomShareSelect
              newUsers={newRecipients}
              setNewUsers={setNewRecipients}
              allUsers={allUsers}
              sharedUsers={viewers}
              // access type isn't relevant for Alerts (currently) but component expects it
              accessType={accessType}
              setAccessType={setAccessType}
              placeholder={'Choose users to receive this alert'}
              title={'Invite Teammates'}
            ></CustomShareSelect>
          </ShareSelectWrapper>
        </UnborderedSection>
        <Section>
          <UserList
            viewers={viewers}
            owners={owners}
            handleUnshare={handleUnshare}
            isOwner={isOwner}
          ></UserList>
        </Section>
      </Body>
      <Footer ref={footerRef}>
        <CancelButton variant="secondary" onClick={() => setIsOpen(false)}>
          Cancel
        </CancelButton>

        <Button variant="primary" onClick={() => manualSubmit()}>
          Share
        </Button>
      </Footer>
      {isSubmitting && (
        <Overlay>
          <Fetcher />
        </Overlay>
      )}
    </Container>
  )
}

const Container = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
`

const Header = styled.div`
  padding: var(--default-padding) var(--default-padding) 5px;
  border-bottom: 1px solid #f2f2f2;
`

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding-bottom: 10px;
`

const CloseTitle = styled.div`
  cursor: pointer;
  margin-top: -20px;
`

const Body = styled.div<{ $headerHeight: number; $footerHeight: number }>`
  display: flex;
  flex-direction: column;
  overflow: auto;
  height: calc(
    100vh - ${(props) => props.$headerHeight}px -
      ${(props) => props.$footerHeight}px - 50px
  );
  padding-bottom: var(--default-padding);
  font-size: 14px;
`

const Section = styled.div`
  display: flex;
  flex-direction: column;
  padding: var(--default-padding-half) var(--default-padding);
  border-bottom: 1px solid #f2f2f2;
`

const UnborderedSection = styled(Section)`
  border: none;
`

const ShareSelectWrapper = styled.div`
  margin-top: -3px;
  width: 100%;
`

const Footer = styled.div`
  background: white;
  display: flex;
  justify-content: space-between;
  margin: 0 var(--default-padding-half);
`

const CancelButton = styled(Button)`
  border-style: dashed;
`

const Overlay = styled.div`
  background: rgba(255, 255, 255, 0.5);
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
`

export default ShareAlert
